import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  TbUser, TbSend, TbUsers, TbBrush, TbAdjustments, TbPlug,
  TbSun, TbMoon, TbDeviceDesktop, TbRoute,
} from "react-icons/tb";
import { useTheme } from "../../ThemeContext";
import { useDismissable, useAnchorRect, returnFocus } from "../hooks/useDismissable";
import "./shell.css";

// The way into settings, behind the identity at the foot of the rail.
//
// Settings no longer has a rail area — this panel and the Administration screen it opens
// are now the only route to them, which is why it lists the two things people reach for
// most directly and sends everything else to the hub.
//
// Two-factor is deliberately absent: ProfileScreen carries the enrolment inline, so a
// row here would be the same screen twice. Sign out is also absent, because it keeps its
// own rail item directly above the trigger.
const PERSONAL = [
  { key: "profile", label: "Profile", icon: TbUser },
  { key: "requests", label: "Feature requests", icon: TbSend },
  // Downloads is not here. It sat in this list briefly, but the scanner binary is
  // something people go looking for by name, and burying it behind an avatar meant
  // hunting for it. It is a rail item above Sign out now, named for what it does.
];

// The four workspace screens, listed directly. The panel is a shortcut surface, and a
// shortcut that lands you on a hub you then have to read is not one — the Administration
// screen is still a click away from the section heading, for anyone who wants the fuller
// page with its descriptions.
const TENANT_ADMIN = [
  { key: "admin-users", label: "Admin panel", icon: TbUsers },
  { key: "branding", label: "Branding", icon: TbBrush },
  { key: "thresholds", label: "Risk thresholds", icon: TbAdjustments },
  { key: "integrations", label: "Integrations", icon: TbPlug },
];

// Sits above the rail rather than inside it: .v2-rail sets overflow:hidden, so a panel
// positioned within it is simply clipped away.
//
// It portals into the shell's own root, NOT into <body>. Every --v2-* token is scoped to
// .v2, and .dark hangs off the same element, so a panel portalled to <body> inherits no
// background, no border and no text colour — it renders as bare text over the page.
const GAP = 8;
const MARGIN = 8;
const PANEL_WIDTH = 460;
const PANEL_WIDTH_SINGLE = 240;

const THEMES = [
  { value: "light", label: "Light", icon: TbSun },
  { value: "dark", label: "Dark", icon: TbMoon },
  { value: "system", label: "System", icon: TbDeviceDesktop },
];

const AccountMenu = ({ open, onClose, triggerRef, me, isAdmin, onNavigate, onStartTour,
                      onPointerEnter, onPointerLeave }) => {
  const { mode, setMode } = useTheme();
  const panelRef = useRef(null);
  const [host, setHost] = useState(null);

  useEffect(() => { setHost(document.getElementById("v2-root")); }, []);

  // Below the drawer breakpoint the panel is a sheet across the foot of the screen rather
  // than a popover beside the rail. It is two columns wide by design — personal settings
  // and tenant admin side by side — and that width is written as an inline style, so on a
  // 320px screen the second column simply hung off the right edge with no way to reach it.
  //
  // Tracked in JS rather than left to CSS because the inline positioning below has to be
  // withheld entirely: an inline `left`/`width` would beat any stylesheet.
  const [sheet, setSheet] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(max-width: 760px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 760px)");
    if (!mq) return undefined;
    const onChange = (e) => setSheet(e.matches);
    // addListener is the Safari < 14 spelling, still present where addEventListener is not.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const close = useCallback(() => {
    returnFocus(panelRef, triggerRef);
    onClose();
  }, [onClose, triggerRef]);

  useDismissable(open, { ref: panelRef, triggerRef, onClose: close });
  const rect = useAnchorRect(open, triggerRef);

  // The anchor rect only matters for the popover; the sheet is placed by the stylesheet.
  if (!open || !host || (!sheet && !rect)) return null;

  const width = isAdmin ? PANEL_WIDTH : PANEL_WIDTH_SINGLE;

  // Anchored to the trigger's top-left, opening upward. Clamped to the viewport so it
  // never runs off the top on a short window or off the right on a narrow one. Not
  // computed in sheet mode, where `rect` may legitimately be absent.
  const left = rect ? Math.min(rect.right + GAP, window.innerWidth - width - MARGIN) : 0;
  const bottom = rect ? Math.max(MARGIN, window.innerHeight - rect.bottom) : 0;

  const go = (key) => { close(); onNavigate(key); };

  const initials = (me?.name || "?").slice(0, 2).toUpperCase();
  // `me.name` is the username, which in most tenants IS the email address. Printing it
  // twice reads as a rendering bug rather than as two facts.
  const showEmail = me?.email && me.email.toLowerCase() !== (me?.name || "").toLowerCase();
  const tenantInitial = (me?.tenant_name || "W").slice(0, 1).toUpperCase();

  // Deliberately role="group", not role="menu". That role promises arrow-key navigation
  // between items, which this does not implement — announcing a menu that does not
  // behave like one is worse for a screen-reader user than announcing nothing special.
  // It is a labelled group of buttons, so that is what it says it is.
  return createPortal(
    <div
      ref={panelRef}
      className={`v2-account${sheet ? " v2-account--sheet" : ""}`}
      role="group"
      aria-label="Account and settings"
      style={sheet ? undefined : { left: Math.max(MARGIN, left), bottom, width }}
      // The panel opens on hover now, so it has to hold itself open while the pointer is
      // inside it — it is portalled out of the rail, so it is not "inside" the trigger as
      // far as the pointer is concerned.
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <section className="v2-account-col">
        <div className="v2-account-label">Personal settings</div>

        <div className="v2-account-identity">
          <span className="v2-avatar">{initials}</span>
          <div style={{ minWidth: 0 }}>
            <div className="v2-account-name">{me?.name || "Signed in"}</div>
            {showEmail && <div className="v2-account-sub">{me.email}</div>}
            {/* v1's dropdown named the role and v2 dropped it. This is the one place it
                belongs: it is the only screen that explains why the admin half of this
                panel is there, or is not. */}
            <div className="v2-account-role">{isAdmin ? "Administrator" : "User"}</div>
          </div>
        </div>

        {/* Switched in place rather than linking to Profile. It is the one thing in this
            panel someone changes repeatedly, and sending them to a settings screen to
            flip it costs more than the control itself. */}
        <div className="v2-account-theme" role="group" aria-label="Appearance">
          {THEMES.map(({ value, label, icon: Icon }) => (
            <button key={value} type="button" title={label} aria-label={label}
                    aria-pressed={mode === value}
                    className={`v2-account-theme-btn${mode === value ? " on" : ""}`}
                    onClick={() => setMode(value)}>
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="v2-account-links">
          {PERSONAL.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button"
                    className="v2-account-link" onClick={() => go(key)}>
              <Icon size={15} />
              <span>{label}</span>
            </button>
          ))}
          {/* Not a screen, so not in PERSONAL: it runs over whatever is on screen. */}
          {onStartTour && (
            <button type="button" className="v2-account-link" onClick={() => { close(); onStartTour(); }}>
              <TbRoute size={15} />
              <span>Take the tour</span>
            </button>
          )}
        </div>
      </section>

      {/* Omitted entirely for a member, not disabled — the same test the rail applies. */}
      {isAdmin && (
        <section className="v2-account-col">
          <div className="v2-account-head">
            <span className="v2-account-label">Tenant admin</span>
            <button type="button" className="v2-account-all" onClick={() => go("administration")}>
              Administration
            </button>
          </div>

          <div className="v2-account-identity">
            {me?.tenant_logo
              ? <img className="v2-account-logo" src={me.tenant_logo} alt="" />
              : <span className="v2-account-mark">{tenantInitial}</span>}
            <div style={{ minWidth: 0 }}>
              <div className="v2-account-name">{me?.tenant_name || "Workspace"}</div>
              <div className="v2-account-sub">Workspace settings and people</div>
            </div>
          </div>

          <div className="v2-account-links">
            {TENANT_ADMIN.map(({ key, label, icon: Icon }) => (
              <button key={key} type="button"
                      className="v2-account-link" onClick={() => go(key)}>
                <Icon size={15} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>,
    host
  );
};

export default AccountMenu;
